/**
 * Shared task-progress row for simulation modules.
 *
 * Displays the canonical pill ("answered / total") plus the thin progress bar
 * underneath. Visual tokens and classes are copied from `QuestionPanel` so
 * Reading/Listening rendered DOM stays identical while the same component
 * can be adopted by Writing panels for parity.
 *
 * The component is purely presentational and owns no state.
 */

import type { ReactNode } from 'react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

export interface SimulationTaskProgressProps {
  /** Left-side label (e.g. "Questions", "Complete the Text", "Essay Task"). */
  readonly labelLeft?: ReactNode;
  /** Optional secondary label shown next to `labelLeft` (e.g. question range). */
  readonly labelRight?: ReactNode;
  /** Current filled/answered count. */
  readonly current: number;
  /** Total items (denominator in the pill + progress bar). */
  readonly total: number;
  /** Optional trailing element rendered to the right of the pill (e.g. an overview button). */
  readonly trailingAccessory?: ReactNode;
  /** Extra classes appended to the root element. */
  readonly className?: string;
}

export function SimulationTaskProgress({
  labelLeft,
  labelRight,
  current,
  total,
  trailingAccessory,
  className,
}: SimulationTaskProgressProps) {
  const percent = total > 0 ? (current / total) * 100 : 0;

  return (
    <div className={cn('shrink-0', className)}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 truncate">
          {labelLeft !== undefined && labelLeft !== null && (
            <span className="font-ui font-bold text-foreground text-sm sm:text-base truncate">
              {labelLeft}
            </span>
          )}
          {labelRight !== undefined && labelRight !== null && (
            <span className="font-ui text-[10px] sm:text-sm text-muted-foreground whitespace-nowrap">
              {labelRight}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {trailingAccessory}
          <div className="px-2.5 sm:px-3 py-1 sm:py-1.5 bg-muted rounded-full">
            <span className="font-ui text-[10px] sm:text-sm font-medium text-muted-foreground whitespace-nowrap">
              <span className="text-foreground font-bold">{current}</span>
              <span className="opacity-60"> / {total}</span>
            </span>
          </div>
        </div>
      </div>

      <Progress value={percent} className="mt-2.5 sm:mt-3 h-1 sm:h-1.5 bg-muted" />
    </div>
  );
}
